import { User } from "@/context/AuthContext";

interface AuthUserSnapshot {
  id?: string;
  email?: string;
  name?: string;
  nameEn?: string;
  nameAr?: string;
  phone?: string;
  roles?: unknown;
}

export interface TokenMembership {
  tenantId: number;
  roleId: number;
  role: string;
  isPrimary: boolean;
}

// Shape of memberships returned by GET /v1/users/me (and /v1/me).
// Richer than the JWT-encoded TokenMembership because the backend
// joins in Tenant and Role rows for display.
export interface MeMembership {
  tenantId: number;
  tenantSlug: string;
  tenantNameAr: string;
  tenantNameEn: string;
  roleId: number;
  roleName: string;
  isPrimary: boolean;
}

export interface MeResponse {
  id?: number | string;
  email?: string;
  name?: string;
  nameEn?: string;
  nameAr?: string;
  phone?: string;
  roles?: unknown;
  memberships?: MeMembership[];
  isPlatformAdmin?: boolean;
}

export const ACTIVE_TENANT_STORAGE_KEY = "active_tenant_id";

const TENANT_REQUIRED_EVENT = "saleem:tenant-required";

export function decodeAuthToken(token: string): {
  sub?: string;
  email?: string;
  roles: string[];
  memberships: TokenMembership[];
  isPlatformAdmin: boolean;
} | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    const roleClaimUri = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
    const roleClaim = payload[roleClaimUri];
    let roles: string[] = [];
    if (Array.isArray(roleClaim)) {
      roles = roleClaim.filter((r): r is string => typeof r === "string");
    } else if (typeof roleClaim === "string") {
      roles = [roleClaim];
    }

    let memberships: TokenMembership[] = [];
    const rawMemberships = payload.memberships;
    if (typeof rawMemberships === "string" && rawMemberships.length) {
      try {
        const parsed = JSON.parse(rawMemberships);
        if (Array.isArray(parsed)) {
          memberships = parsed
            .map((m): TokenMembership | null => {
              if (!m || typeof m !== "object") return null;
              const o = m as Record<string, unknown>;
              const tenantId = Number(o.tenantId);
              if (!Number.isFinite(tenantId)) return null;
              return {
                tenantId,
                roleId: Number(o.roleId) || 0,
                role: typeof o.role === "string" ? o.role : "",
                isPrimary: Boolean(o.isPrimary),
              };
            })
            .filter((m): m is TokenMembership => m !== null);
        }
      } catch {
        // Ignore malformed memberships claim — backend should always send valid JSON.
      }
    } else if (Array.isArray(rawMemberships)) {
      memberships = rawMemberships as TokenMembership[];
    }

    const isPlatformAdmin = payload.platformAdmin === "true" || payload.platformAdmin === true;

    return {
      sub: typeof payload.sub === "string" ? payload.sub : undefined,
      email: typeof payload.email === "string" ? payload.email : undefined,
      roles,
      memberships,
      isPlatformAdmin,
    };
  } catch {
    return null;
  }
}

// API Client for Saleem Care Backend
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:5108";

export interface ApiResponse<T> {
  data?: T;
  error?: {
    message?: string;
  };
}

interface ApiError {
  message?: string;
  error?: {
    message?: string;
  };
}

// Structured 400 response body for tenant-required endpoints.
// See TENANT_OPTIONAL_PATIENT_DASHBOARD.md §1.1.
export interface ApiErrorBody {
  success: false;
  errorCode?: "TENANT_HEADER_REQUIRED" | "NO_TENANT_MEMBERSHIPS" | string;
  action?: "SELECT_TENANT" | "ADD_TENANT" | string;
  allowedTenantIds?: number[];
  message: string;
  errors?: Record<string, string[]>;
}

interface BackendEnvelope<T> {
  success: boolean;
  data?: T | null;
  message?: string | null;
  errors?: unknown;
}

// Paged backend responses extend the envelope with pagination siblings of `data`:
//   { success, data: [...], page, pageSize, totalCount, totalPages, ... }
// We reshape these into `{ items, page, pageSize, total, totalPages }` so callers
// can keep using the familiar `response.items` shape.
interface PagedEnvelope<T> extends BackendEnvelope<T[]> {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages?: number;
}

function isEnvelope(value: unknown): value is BackendEnvelope<unknown> {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.success === "boolean" &&
    ("data" in v || "message" in v || "errors" in v)
  );
}

function isPagedEnvelope(env: BackendEnvelope<unknown>): env is PagedEnvelope<unknown> {
  const v = env as unknown as Record<string, unknown>;
  return (
    Array.isArray(v.data) &&
    typeof v.page === "number" &&
    typeof v.pageSize === "number" &&
    typeof v.totalCount === "number"
  );
}

function extractEnvelopeError(envelope: BackendEnvelope<unknown>): string | undefined {
  const { errors, message } = envelope;
  if (errors) {
    if (typeof errors === "string") return errors;
    if (Array.isArray(errors)) {
      const joined = errors
        .map((e) => (typeof e === "string" ? e : (e as { message?: string })?.message))
        .filter(Boolean)
        .join(", ");
      if (joined) return joined;
    } else if (typeof errors === "object") {
      // Could be { field: [msg, ...] } or { field: msg }
      const parts: string[] = [];
      for (const v of Object.values(errors as Record<string, unknown>)) {
        if (typeof v === "string") parts.push(v);
        else if (Array.isArray(v)) parts.push(...v.filter((x): x is string => typeof x === "string"));
      }
      if (parts.length) return parts.join(", ");
    }
  }
  return message ?? undefined;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    _isRetry: boolean = false
  ): Promise<ApiResponse<T>> {
    const token = localStorage.getItem("auth_token");

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string> | undefined),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    if (!headers["X-Tenant-Id"]) {
      const tenantId = localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY);
      if (tenantId) headers["X-Tenant-Id"] = tenantId;
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      // 401 interceptor: try refresh-token once, retry, otherwise logout + redirect
      if (
        response.status === 401 &&
        !_isRetry &&
        endpoint !== "/v1/auth/refresh-token" &&
        endpoint !== "/v1/auth/login"
      ) {
        const refreshed = await this.tryRefreshToken();
        if (refreshed) {
          return this.request<T>(endpoint, options, true);
        }
        this.logout();
        if (typeof window !== "undefined" && window.location.pathname !== "/auth") {
          window.location.href = "/auth";
        }
        return { error: { message: "Session expired. Please log in again." } };
      }

      // Check if response has content
      const contentType = response.headers.get("content-type");
      const isJson = contentType?.includes("application/json");

      // Get response text first
      const text = await response.text();

      // If empty response and status is ok, return empty data
      if (!text && response.ok) {
        return { data: {} as T };
      }

      // Try to parse as JSON if it's JSON content type or if text exists
      let data: unknown;
      if (isJson || text) {
        try {
          data = text ? JSON.parse(text) : {};
        } catch (parseError) {
          // If parsing fails and response is not ok, return error
          if (!response.ok) {
            return {
              error: {
                message: `HTTP ${response.status}: ${response.statusText}`,
              },
            };
          }
          // If parsing fails but response is ok, return the text as error
          return {
            error: {
              message: "Invalid JSON response from server",
            },
          };
        }
      } else {
        data = {};
      }

      if (!response.ok) {
        // Handle specific status codes with better messages
        let errorMessage: string | undefined;

        if (isEnvelope(data)) {
          errorMessage = extractEnvelopeError(data);
        }

        errorMessage =
          errorMessage ||
          (data as ApiError)?.error?.message ||
          (typeof (data as ApiError)?.error === "string"
            ? ((data as unknown as { error: string }).error)
            : undefined) ||
          (data as ApiError)?.message ||
          `HTTP ${response.status}: ${response.statusText}`;

        if (response.status === 403) {
          errorMessage = "Access denied. Admin role required to access this resource.";
        } else if (response.status === 401) {
          errorMessage = "Unauthorized. Please log in again.";
        } else if (response.status === 404) {
          errorMessage = "Resource not found.";
        } else if (response.status === 400) {
          // Structured tenant-required error from TenantResolutionMiddleware.
          // See TENANT_OPTIONAL_PATIENT_DASHBOARD.md §2.1.
          const body = data as ApiErrorBody | undefined;
          const code = body?.errorCode;
          if (code === "TENANT_HEADER_REQUIRED") {
            // Multi-tenant user hasn't picked one. The dashboard does not have a
            // dedicated /select-hospital route; the header TenantSwitcher is the
            // existing UX. Dispatch the tenant-required event so DashboardLayout
            // surfaces a toast pointing the user at the switcher.
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent(TENANT_REQUIRED_EVENT, {
                  detail: {
                    message: errorMessage,
                    allowedTenantIds: body?.allowedTenantIds,
                  },
                })
              );
            }
          } else if (code === "NO_TENANT_MEMBERSHIPS") {
            // Staff somehow lost every membership (all suspended). Treat like 401:
            // sign out and bounce to /auth. Message is preserved on the returned
            // error so the caller can render it on the login page if desired.
            this.logout();
            if (typeof window !== "undefined" && window.location.pathname !== "/auth") {
              window.location.href = "/auth";
            }
            return {
              error: {
                message:
                  body?.message ||
                  "Your account is not a member of any hospital. Please contact your administrator.",
              },
            };
          } else if (
            typeof errorMessage === "string" &&
            errorMessage.includes("Tenant context required")
          ) {
            // Backwards-compat with the pre-TENANT_OPTIONAL backend, which sent
            // a plain message instead of errorCode. Safe to remove once the
            // backend PR has shipped to all environments.
            if (typeof window !== "undefined") {
              window.dispatchEvent(
                new CustomEvent(TENANT_REQUIRED_EVENT, { detail: { message: errorMessage } })
              );
            }
          }
        }

        return {
          error: {
            message: errorMessage,
          },
        };
      }

      // Backend wraps every successful response in { success, data, message, errors }.
      // Unwrap it so callers see the inner payload directly.
      if (isEnvelope(data)) {
        if (!data.success) {
          return {
            error: {
              message: extractEnvelopeError(data) || "Request failed",
            },
          };
        }
        if (isPagedEnvelope(data)) {
          return {
            data: {
              items: data.data ?? [],
              page: data.page,
              pageSize: data.pageSize,
              total: data.totalCount,
              totalPages: data.totalPages,
            } as T,
          };
        }
        return { data: (data.data ?? {}) as T };
      }

      return { data: data as T };
    } catch (error: unknown) {
      return {
        error: {
          message: (error as Error).message || "Network error occurred",
        },
      };
    }
  }

  async login(email: string, password: string) {
    const response = await this.request<{ token: string; user?: AuthUserSnapshot }>(
      "/v1/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ email, password }),
      }
    );

    if (response.data?.token) {
      localStorage.setItem("auth_token", response.data.token);
      const decoded = decodeAuthToken(response.data.token);
      if (decoded) {
        const u = response.data.user;
        const displayName = u?.name ?? u?.nameEn ?? u?.nameAr ?? "";
        localStorage.setItem(
          "auth_user",
          JSON.stringify({
            id: decoded.sub || u?.id,
            email: u?.email ?? email,
            name: displayName,
            nameEn: u?.nameEn ?? displayName,
            nameAr: u?.nameAr ?? displayName,
            roles: decoded.roles,
            memberships: decoded.memberships,
            isPlatformAdmin: decoded.isPlatformAdmin,
          })
        );
        this.syncActiveTenant(decoded.memberships);
      }
    }

    return response;
  }

  async register(name: string, email: string, password: string) {
    const response = await this.request<{ token: string; user?: AuthUserSnapshot }>(
      "/v1/auth/register",
      {
        method: "POST",
        body: JSON.stringify({ name, email, password }),
      }
    );

    if (response.data?.token) {
      localStorage.setItem("auth_token", response.data.token);
      const decoded = decodeAuthToken(response.data.token);
      if (decoded) {
        const u = response.data.user;
        const displayName = u?.name ?? u?.nameEn ?? u?.nameAr ?? name;
        localStorage.setItem(
          "auth_user",
          JSON.stringify({
            id: decoded.sub || u?.id,
            email: u?.email ?? email,
            name: displayName,
            nameEn: u?.nameEn ?? displayName,
            nameAr: u?.nameAr ?? displayName,
            roles: decoded.roles,
            memberships: decoded.memberships,
            isPlatformAdmin: decoded.isPlatformAdmin,
          })
        );
        this.syncActiveTenant(decoded.memberships);
      }
    }

    return response;
  }

  private syncActiveTenant(memberships: TokenMembership[]) {
    if (memberships.length === 0) {
      localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
      return;
    }
    const current = Number(localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY));
    if (current && memberships.some((m) => m.tenantId === current)) return;
    const fallback = memberships.find((m) => m.isPrimary)?.tenantId ?? memberships[0].tenantId;
    localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, String(fallback));
  }

  async getCurrentUser() {
    const response = await this.request<MeResponse>("/v1/users/me");
    // /me is the authoritative source for the caller's active memberships.
    // Sync the X-Tenant-Id we send on subsequent requests off the response,
    // not the (possibly stale) JWT claim.
    const memberships = response.data?.memberships;
    if (memberships !== undefined) {
      this.syncActiveTenant(
        memberships.map((m) => ({
          tenantId: m.tenantId,
          roleId: m.roleId,
          role: m.roleName,
          isPrimary: m.isPrimary,
        }))
      );
    }
    return response;
  }

  async refreshToken() {
    return this.request<{ token: string }>("/v1/auth/refresh-token", {
      method: "POST",
    });
  }

  private async tryRefreshToken(): Promise<boolean> {
    try {
      const res = await this.refreshToken();
      if (res.data?.token) {
        localStorage.setItem("auth_token", res.data.token);
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  }

  logout() {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
  }

  getToken(): string | null {
    return localStorage.getItem("auth_token");
  }

  getUser(): User | null {
    const userStr = localStorage.getItem("auth_user");
    if (userStr) {
      try {
        return JSON.parse(userStr) as User;
      } catch (e) {
        return null;
      }
    }
    return null;
  }
}

export const apiClient = new ApiClient(API_BASE_URL);

export const TENANT_REQUIRED_EVENT_NAME = TENANT_REQUIRED_EVENT;
